import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import BottomFixedContainer from '@/components/molecule/BottomFixedContainer';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import { BUTTON_CONST } from '@/src/constants/buttons';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import DetailSection, {
  DetailSectionContent,
} from '@/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/components/organism/DetailSection/renderer';
import { getCustomersByIdAPI } from '@/src/apis/rest/customers';
import { NAV_CONST } from '@/src/constants/nav';
import { CustomersDto, CustomerStatus } from '@/src/models/CustomersDto';
import { useCallback, useState } from 'react';
import SectionCard from '@/components/molecule/SectionCard';
import StatusManageDialog from '@/src/pages/Customer/components/StatusManageDialog';
import CreditManageDialog from '@/src/pages/Customer/components/CreditManageDialog';
import CustomerDetailTabTable from '@/src/pages/Customer/components/CustomerDetailTabTable';
import { UNIT_STRINGS } from '@/src/constants/unit';
import PATH from '@/src/routes/path';
import Typo from '@/components/atom/Typo';
import {
  ACCOUNT_PROVIDER_OPTIONS,
  CUSTOMER_STATUS_OPTIONS,
  OPTIONS_CONST,
} from '@/src/constants/options';

const BACK_PATH = PATH.NORMAL_CUSTOMER_LIST;

function NormalCustomerDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);

  const { data: customerData, refetch: refetchCustomerData } = useQuery({
    queryKey: ['customers', 'normal', id],
    queryFn: () => getCustomersByIdAPI(Number(id)),
    enabled: !!id,
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  const withdrawalContents = useCallback(() => {
    return [
      [
        {
          key: 'withdrawalAt',
          lable: '탈퇴 일시',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.withdrawalHistory?.updatedAt || '-',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'nick',
          lable: '닉네임',
          valueSetter: (data: CustomersDto | undefined) => data?.nick || '-',
        },
      ],
      [
        {
          key: 'status',
          lable: '회원 상태',
          valueSetter: (data: CustomersDto | undefined) => data?.status || '',
          render: (value: string) => {
            const status: CustomerStatus = value as CustomerStatus;
            return (
              <Typo typoType="b16r" typoColor="gray6">
                {status ? CUSTOMER_STATUS_OPTIONS[status] : '-'}
              </Typo>
            );
          },
        },
        {
          key: 'updateReason',
          lable: '탈퇴 사유',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.withdrawalHistory?.updateReason || '-',
        },
        {
          key: 'administrator',
          lable: '담당 관리자',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.withdrawalHistory?.administrator?.name || '-',
        },
      ],
    ];
  }, []);

  const suspendedContents = useCallback(() => {
    return [
      [
        {
          key: 'createdAt',
          lable: '가입 일시',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'signupSourceName',
          lable: '가입 경로',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.signupSource === null
              ? '에코야얼스'
              : data?.signupSource?.partnershipCompany?.name || '-',
        },
        {
          key: 'nick',
          lable: '닉네임',
          valueSetter: (data: CustomersDto | undefined) => data?.nick || '-',
        },
        {
          key: 'name',
          lable: '회원 이름',
          valueSetter: (data: CustomersDto | undefined) => data?.name || '-',
        },
        {
          key: 'channel',
          lable: '가입 채널',
          valueSetter: (data: CustomersDto | undefined) => {
            const provider = data?.account?.accountProviders?.[0]?.provider;
            if (!provider) return '-';
            return (
              ACCOUNT_PROVIDER_OPTIONS[
                provider as keyof typeof ACCOUNT_PROVIDER_OPTIONS
              ] || '-'
            );
          },
        },
        {
          key: 'reportCnt',
          lable: '블라인드 수',
          valueSetter: (data: CustomersDto | undefined) => {
            const commentCount = data?.communityCommentReportCnt || 0;
            const postingCount = data?.communityPostReportCnt || 0;
            return `${(commentCount + postingCount).toLocaleString()}회`;
          },
        },
      ],
      [
        {
          key: 'status',
          lable: '회원 상태',
          valueSetter: (data: CustomersDto | undefined) => data?.status || '',
          render: (status: string) => {
            return (
              <>
                <Typo typoType="b16r" typoColor="gray6">
                  {status
                    ? CUSTOMER_STATUS_OPTIONS[status as CustomerStatus]
                    : '-'}
                </Typo>
                <Button
                  buttonColor="secondary"
                  buttonSize="xSmall"
                  onClick={() => setIsStatusDialogOpen(true)}
                >
                  회원 상태 관리
                </Button>
              </>
            );
          },
        },
        {
          key: 'suspendedAt',
          lable: '정지 일시',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.suspendHistory?.updatedAt || '-',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'updateReason',
          lable: '정지 사유',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.suspendHistory?.updateReason || '-',
        },
        {
          key: 'credit',
          lable: '보유 크레딧',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.credit?.currentCredit.toLocaleString() || '0',
          render: (credit: string) => {
            return (
              <>
                <Typo typoType="b16r" typoColor="gray6">
                  {`${credit}${UNIT_STRINGS.CREDIT}`}
                </Typo>
                <Button
                  buttonColor="secondary"
                  buttonSize="xSmall"
                  onClick={() => setIsCreditDialogOpen(true)}
                >
                  크레딧 지급/차감 관리
                </Button>
              </>
            );
          },
        },
        {
          key: 'isMarketingAgreed',
          lable: '마케팅 활용 동의 여부',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.account?.accountOptionalTerms ? 'true' : 'false',
          render: DetailSctionRenderer.BADGE({
            options: [
              {
                label: OPTIONS_CONST.agreed,
                key: 'true',
                badgeColor: 'lightGreen',
              },
              {
                label: OPTIONS_CONST.notAgreed,
                key: 'false',
                badgeColor: 'lightGray',
              },
            ],
          }),
        },
      ],
    ];
  }, []);

  const normalContents = useCallback(() => {
    return [
      [
        {
          key: 'createdAt',
          lable: '가입 일시',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
        {
          key: 'signupSourceName',
          lable: '가입 경로',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.signupSource === null
              ? '에코야얼스'
              : data?.signupSource?.partnershipCompany?.name || '-',
        },
        {
          key: 'nick',
          lable: '닉네임',
          valueSetter: (data: CustomersDto | undefined) => data?.nick || '-',
        },
        {
          key: 'channel',
          lable: '가입 채널',
          valueSetter: (data: CustomersDto | undefined) => {
            const provider = data?.account?.accountProviders?.[0]?.provider;
            if (!provider) return '-';
            return (
              ACCOUNT_PROVIDER_OPTIONS[
                provider as keyof typeof ACCOUNT_PROVIDER_OPTIONS
              ] || '-'
            );
          },
        },
        {
          key: 'name',
          lable: '회원 이름',
          valueSetter: (data: CustomersDto | undefined) => data?.name || '-',
        },
        {
          key: 'reportCnt',
          lable: '블라인드 수',
          valueSetter: (data: CustomersDto | undefined) => {
            const commentCount = data?.communityCommentReportCnt || 0;
            const postingCount = data?.communityPostReportCnt || 0;
            return `${(commentCount + postingCount).toLocaleString()}회`;
          },
        },
      ],
      [
        {
          key: 'status',
          lable: '회원 상태',
          valueSetter: (data: CustomersDto | undefined) => data?.status || '',
          render: (status: string) => {
            return (
              <>
                <Typo typoType="b16r" typoColor="gray6">
                  {status
                    ? CUSTOMER_STATUS_OPTIONS[status as CustomerStatus]
                    : '-'}
                </Typo>
                {status !== 'withdrawal' && (
                  <Button
                    buttonColor="secondary"
                    buttonSize="xSmall"
                    onClick={() => setIsStatusDialogOpen(true)}
                  >
                    회원 상태 관리
                  </Button>
                )}
              </>
            );
          },
        },
        {
          key: 'credit',
          lable: '보유 크레딧',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.credit?.currentCredit.toLocaleString() || '0',
          render: (credit: string) => {
            return (
              <>
                <Typo typoType="b16r" typoColor="gray6">
                  {`${credit}${UNIT_STRINGS.CREDIT}`}
                </Typo>
                {customerData?.status !== 'withdrawal' && (
                  <Button
                    buttonColor="secondary"
                    buttonSize="xSmall"
                    onClick={() => setIsCreditDialogOpen(true)}
                  >
                    크레딧 지급/차감 관리
                  </Button>
                )}
              </>
            );
          },
        },
        {
          key: 'isMarketingAgreed',
          lable: '마케팅 활용 동의 여부',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.account?.accountOptionalTerms ? 'true' : 'false',
          render: DetailSctionRenderer.BADGE({
            options: [
              {
                label: OPTIONS_CONST.agreed,
                key: 'true',
                badgeColor: 'lightGreen',
              },
              {
                label: OPTIONS_CONST.notAgreed,
                key: 'false',
                badgeColor: 'lightGray',
              },
            ],
          }),
        },
        {
          key: 'lastLoggedInAt',
          lable: '마지막 로그인 일시',
          valueSetter: (data: CustomersDto | undefined) =>
            data?.account?.lastLoggedInAt || '',
          render: DetailSctionRenderer.DATE({
            format: 'YYYY-MM-DD HH:mm:ss',
          }),
        },
      ],
    ];
  }, [customerData?.status]);

  const getContents = useCallback((): DetailSectionContent<
    CustomersDto | undefined
  >[][] => {
    if (customerData?.status === 'withdrawal') return withdrawalContents();
    if (customerData?.status === 'suspended') return suspendedContents();
    if (customerData?.status === 'normal') return normalContents();
    return normalContents();
  }, [normalContents, suspendedContents, withdrawalContents, customerData]);

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <StatusManageDialog
        submitCallback={refetchCustomerData}
        customer={customerData}
        isOpen={isStatusDialogOpen}
        setOpen={setIsStatusDialogOpen}
      />
      <CreditManageDialog
        submitCallback={refetchCustomerData}
        customer={customerData}
        isOpen={isCreditDialogOpen}
        setOpen={setIsCreditDialogOpen}
      />
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.normalCustomersManagement,
          NAV_CONST.normalCustomersDetail,
        ]}
        pageTitle={NAV_CONST.normalCustomersDetail}
      />
      <div className="mb-24 flex flex-col gap-6">
        <DetailSection
          key={customerData?.status}
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                기본 정보
              </span>
            </div>
          }
          data={customerData}
          contents={getContents()}
        />
        <div className="flex flex-col">
          <SectionCard
            cardTitle="추가 정보"
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <CustomerDetailTabTable customerId={id!} />
          </SectionCard>
        </div>
      </div>
      {renderBottomFixedContent()}
    </>
  );
}

export default NormalCustomerDetailPage;
